import { Button } from '@nautilusbio/nb-design-system/Button';
import Group from '@nautilusbio/nb-design-system/Group';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Textarea } from '@nautilusbio/nb-design-system/Textarea';
import { UseMutationResult } from '@tanstack/react-query';
import { Check, Loader, X } from 'lucide-react';
import React, { ChangeEvent, type ComponentType, type FC, type ReactNode } from 'react';

import type { MutationContext, UpdateVariables } from '../../../hooks';
import type { InstrumentRunPublic } from '../../../types';
import styles from '../styles.module.css';

interface FieldState {
  value: string;
  meta: {
    errors: Array<string | undefined>;
  };
}

interface Field {
  name: string;
  state: FieldState;
  handleBlur: () => void;
  handleChange: (value: string) => void;
}

interface FormState {
  canSubmit: boolean;
  isSubmitting: boolean;
}

export interface DescriptionEditFormProps {
  form: {
    Field: ComponentType<{
      name: string;
      children: (field: Field) => ReactNode;
    }>;
    Subscribe: ComponentType<{
      selector: (state: FormState) => readonly [boolean, boolean];
      children: (value: readonly [boolean, boolean]) => ReactNode;
    }>;
    handleSubmit: () => Promise<void>;
  };
  fieldName: string;
  title: string;
  mutation?: UseMutationResult<InstrumentRunPublic, Error, UpdateVariables, MutationContext>;
  ariaLabel?: string;
  onCancel: () => void;
  fullscreen?: boolean;
}

const DescriptionEditForm: FC<DescriptionEditFormProps> = ({
  form,
  fieldName,
  title,
  mutation,
  ariaLabel = 'Edit description',
  onCancel,
  fullscreen = false,
}) => (
  <form
    className={fullscreen ? `${styles.editForm} ${styles.fullscreenEditForm}` : styles.editForm}
    onSubmit={async e => {
      e.preventDefault();
      e.stopPropagation();
      await form.handleSubmit();
    }}
  >
    <form.Field name={fieldName}>
      {(field: Field) => (
        <Stack fullWidth gap={2} orientation="vertical">
          <div className={styles.textareaContainer}>
            <Textarea
              autoComplete="off"
              className={styles.textarea}
              data-testid={`textarea-${field.name}`}
              id={field.name}
              label={{
                children: title,
                className: styles.visuallyHidden,
                id: `label-${field.name}`,
                htmlFor: field.name,
              }}
              name={field.name}
              onBlur={field.handleBlur}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => field.handleChange(e.target.value)}
              rows={fullscreen ? 20 : 4}
              value={field.state.value}
            />

            <Group className={styles.actionButtonsOverlay} orientation="horizontal">
              <form.Subscribe selector={(state: FormState) => [state.canSubmit, state.isSubmitting] as const}>
                {([canSubmit, isSubmitting]: readonly [boolean, boolean]) => (
                  <Button
                    aria-label={ariaLabel}
                    className={styles.actionBtn}
                    data-testid="submit-btn"
                    isDisabled={!canSubmit || isSubmitting}
                    size="xs"
                    type="submit"
                    visual="success"
                  >
                    {isSubmitting || mutation?.isPending ? (
                      <Loader className={styles.spinner} size={16} />
                    ) : (
                      <Check size={16} />
                    )}
                  </Button>
                )}
              </form.Subscribe>

              <Button
                aria-label="Cancel editing"
                className={styles.actionBtn}
                data-testid="cancel-btn"
                onPress={onCancel}
                size="xs"
                visual="default"
              >
                <X size={16} />
              </Button>
            </Group>
          </div>

          {field.state.meta.errors.length > 0 && (
            <Text as="span" className={styles.error}>
              {field.state.meta.errors.join(', ')}
            </Text>
          )}

          {mutation?.isError && (
            <Text as="span" className={styles.error}>
              {mutation.error?.message || 'Failed to update'}
            </Text>
          )}
        </Stack>
      )}
    </form.Field>
  </form>
);

export default DescriptionEditForm;
